import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { addVehicle, apiFetch } from '@/src/api/apiService';
import { API_ENDPOINTS, API_BASE } from '../api/config';

interface EmploymentInfo {
  company_name: string;
  company_nipc: string;
  company_email?: string;
  company_phone?: string;
}

interface VehicleInfo {
  make: string;
  model: string;
  year: string;
  licensePlate: string;
  fuelType: string;
  pendingActivation?: boolean;
}

interface VehicleSetupResult {
  pendingActivation: boolean;
}

interface OnboardingState {
  hasCompleted: boolean;
  hasEmployment: boolean | null;
  employmentInfo: EmploymentInfo | null;
  hasVehicle: boolean | null;
  vehicleInfo: VehicleInfo | null;
  permissions: {
    notifications: boolean;
    locationForeground: boolean;
    locationBackground: boolean;
  };
  isLoading: {
    employment: boolean;
    vehicle: boolean;
  };
}

interface OnboardingContextType {
  state: OnboardingState;
  setHasEmployment: (value: boolean) => void;
  setHasVehicle: (value: boolean) => void;
  setVehicleInfo: (info: VehicleInfo) => Promise<VehicleSetupResult>;
  setPermission: (key: 'notifications' | 'locationForeground' | 'locationBackground', value: boolean) => void;
  completeOnboarding: (permissions?: OnboardingState['permissions']) => Promise<void>;
  checkOnboardingStatus: () => Promise<boolean>;
  fetchEmploymentInfo: () => Promise<void>;
  fetchVehicleInfo: () => Promise<void>;
}

const defaultState: OnboardingState = {
  hasCompleted: false,
  hasEmployment: null,
  employmentInfo: null,
  hasVehicle: null,
  vehicleInfo: null,
  permissions: {
    notifications: false,
    locationForeground: false,
    locationBackground: false,
  },
  isLoading: {
    employment: false,
    vehicle: false,
  },
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const buildNetworkDiagnosticError = (endpoint: string, originalError?: unknown) => {
  const now = new Date().toISOString();
  const originalMessage = originalError instanceof Error ? originalError.message : String(originalError || 'unknown_error');
  return new Error(
    `Falha de rede ao carregar dados de onboarding. endpoint=${endpoint} api_base=${API_BASE} ts=${now} cause=${originalMessage}`
  );
};

const ONBOARDING_EMPLOYMENT_NETWORK_ERROR = 'ONBOARDING_EMPLOYMENT_NETWORK_ERROR';
const ONBOARDING_USER_KEY = 'onboarding:user';

const getOnboardingDataStorageKey = async (): Promise<string> => {
  const onboardingUser = await AsyncStorage.getItem(ONBOARDING_USER_KEY);
  return onboardingUser ? `onboardingData:${onboardingUser}` : 'onboardingData';
};

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<OnboardingState>(defaultState);

  const setHasEmployment = (value: boolean) => {
    setState((prev) => ({
      ...prev,
      hasEmployment: value,
    }));
  };

  const setHasVehicle = (value: boolean) => {
    setState((prev) => ({
      ...prev,
      hasVehicle: value,
    }));
  };

  const setVehicleInfo = async (info: VehicleInfo) => {
    try {
      // Check if user already has a vehicle
      const hasVehicle = state.hasVehicle === true
                        && state.vehicleInfo &&
                        state.vehicleInfo.licensePlate !== '' &&
                        state.vehicleInfo.make !== '';

      // Only attempt to add if user doesn't have a vehicle
      let pendingActivation = info.pendingActivation === true;
      if (!hasVehicle) {
        const response = await addVehicle(
          info.licensePlate,
          info.fuelType,
          info.make,
          info.model,
          parseInt(info.year)
        );

        if (!response.ok && response.status !== 201) {
          throw new Error('Failed to add vehicle');
        }
        pendingActivation = response.data?.pending_activation === true;
      }
      setState((prev) => ({
        ...prev,
        vehicleInfo: { ...info, pendingActivation },
        hasVehicle: true,
      }));
      return { pendingActivation };
    } catch (error) {
      console.error('Error setting vehicle info:', error);
      throw error;
    }
  };

  const setPermission = (
    key: 'notifications' | 'locationForeground' | 'locationBackground',
    value: boolean
  ) => {
    setState((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: value,
      },
    }));
  };

  const fetchEmploymentInfo = async () => {
    try {
      setState(prev => ({
        ...prev,
        isLoading: {
          ...prev.isLoading,
          employment: true
        }
      }));

      const response = await apiFetch<any>(API_ENDPOINTS.USER_DETAILS, { method: 'GET' }, true, false);
      const userData = response.data;

      if (response.ok && userData) {
        const hasEmployments = userData.employments && userData.employments.length > 0;
        
        if (hasEmployments) {
          const activeEmployment = userData.employments.find((emp: any) => emp.is_active);
          const firstEmployment = activeEmployment || userData.employments[0];
          
          const employmentInfo = {
            company_name: userData.company?.name || firstEmployment.company_name || 'Unknown Company',
            company_nipc: firstEmployment.company_nipc || '',
            company_email:
              userData.company?.email != null
                ? String(userData.company.email)
                : firstEmployment.company_email != null
                  ? String(firstEmployment.company_email)
                  : '',
            company_phone:
              userData.company?.phone != null
                ? String(userData.company.phone)
                : firstEmployment.company_phone != null
                  ? String(firstEmployment.company_phone)
                  : ''
          };
          
          
          setState(prev => ({
            ...prev,
            hasEmployment: true,
            employmentInfo: employmentInfo,
            isLoading: {
              ...prev.isLoading,
              employment: false
            }
          }));
        } else {
          setState(prev => ({
            ...prev,
            hasEmployment: false,
            isLoading: {
              ...prev.isLoading,
              employment: false
            }
          }));
        }
      } else {
        setState(prev => ({
          ...prev,
          hasEmployment: null,
          isLoading: {
            ...prev.isLoading,
            employment: false
          }
        }));

        if (response.status === 0 && response.errorType === 'NETWORK') {
          throw new Error(ONBOARDING_EMPLOYMENT_NETWORK_ERROR);
        }

        throw new Error(userData?.message || userData?.detail || response.errorMessage || 'Failed to fetch employment info');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        hasEmployment: null,
        isLoading: {
          ...prev.isLoading,
          employment: false
        }
      }));

      const diagnosticError = buildNetworkDiagnosticError(API_ENDPOINTS.USER_DETAILS, error);
      console.error(diagnosticError);

      if (error instanceof Error && error.message === ONBOARDING_EMPLOYMENT_NETWORK_ERROR) {
        throw error;
      }

      throw error instanceof Error ? error : new Error('Failed to fetch employment info');
    }
  };

  const fetchVehicleInfo = async () => {
    try {
      setState(prev => ({
        ...prev,
        isLoading: {
          ...prev.isLoading,
          vehicle: true
        }
      }));

      const response = await apiFetch<any>(API_ENDPOINTS.DRIVER_VEHICLES, { method: 'GET' }, true, false);
      const vehicleData = response.data;

      if (response.ok && vehicleData) {
        if (vehicleData.has_vehicles && vehicleData.vehicles && vehicleData.vehicles.length > 0) {
          const vehicle = vehicleData.vehicles[0];
          
          const vehicleInfo = {
            make: vehicle.make || '',
            model: vehicle.model || '',
            year: vehicle.year ? vehicle.year.toString() : '',
            licensePlate: vehicle.registration_number || '',
            fuelType: vehicle.fuel_type || 'petrol',
            pendingActivation: vehicle.pending_activation === true,
          };
          
          setState(prev => ({
            ...prev,
            hasVehicle: true,
            vehicleInfo: vehicleInfo,
            isLoading: {
              ...prev.isLoading,
              vehicle: false
            }
          }));
        } else {
          setState(prev => ({
            ...prev,
            hasVehicle: false,
            vehicleInfo: null,
            isLoading: {
              ...prev.isLoading,
              vehicle: false
            }
          }));
        }
      } else {
        setState(prev => ({
          ...prev,
          hasVehicle: null,
          isLoading: {
            ...prev.isLoading,
            vehicle: false
          }
        }));

        if (response.status === 0) {
          console.error(buildNetworkDiagnosticError(API_ENDPOINTS.DRIVER_VEHICLES));
        }
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        hasVehicle: null,
        isLoading: {
          ...prev.isLoading,
          vehicle: false
        }
      }));
      console.error(buildNetworkDiagnosticError(API_ENDPOINTS.DRIVER_VEHICLES, error));
    }
  };

  const completeOnboarding = async (permissions?: OnboardingState['permissions']) => {
    const onboardingData = {
      ...state,
      hasCompleted: true,
      permissions: permissions ?? state.permissions,
    };

    const onboardingDataKey = await getOnboardingDataStorageKey();
    await AsyncStorage.setItem(onboardingDataKey, JSON.stringify(onboardingData));

    setState((prev) => ({
      ...prev,
      hasCompleted: true,
      permissions: permissions ?? prev.permissions,
    }));
  };

  const checkOnboardingStatus = async () => {
    try {
      const onboardingDataKey = await getOnboardingDataStorageKey();
      const savedData = await AsyncStorage.getItem(onboardingDataKey);

      if (savedData) {
        const parsedData = JSON.parse(savedData) as OnboardingState;
        setState(prev => ({
          ...parsedData,
          isLoading: prev.isLoading
        }));
        return parsedData.hasCompleted;
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        state,
        setHasEmployment,
        setHasVehicle,
        setVehicleInfo,
        setPermission,
        completeOnboarding,
        checkOnboardingStatus,
        fetchEmploymentInfo,
        fetchVehicleInfo,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}; 